// Canonical RecurrenceRule serialisation + the auto-open decision helpers
// that drive "after save, should we auto-open the Generate Occurrences
// dialog?". Pulled out of the tab files so they're pure + unit-testable
// in isolation. Both deliverable and meeting tabs consume these.

import type { RecurrenceRule } from "../../../../types";

/** Stable string representation of a RecurrenceRule. Equal rules produce
 *  equal strings. Used by:
 *  - GenerateOccurrencesDialog: React Query cache key so the preview
 *    refetches when the rule mutates between opens.
 *  - shouldAutoOpenOnUpdate (below) to decide if the schedule changed. */
export function serialiseRule(rule?: RecurrenceRule): string {
  if (!rule) return "";
  return `${rule.frequency}|${rule.interval ?? 1}|${rule.startDate}|${rule.endDate ?? ""}`;
}

/** After a successful Create, should we auto-open the Generate dialog?
 *  Yes iff the created item has a recurrenceRule (scheduled — occurrences
 *  are pending materialisation). Non-recurring items have nothing to
 *  generate. Accepts undefined for safety. */
export function shouldAutoOpenOnCreate(
  created: { recurrenceRule?: RecurrenceRule } | undefined,
): boolean {
  return !!created?.recurrenceRule;
}

/** After a successful Edit, should we auto-open the Generate dialog?
 *  Yes iff the update explicitly touched recurrenceRule AND the new rule
 *  differs from the stored one AND the new rule is non-empty.
 *  - If "recurrenceRule" isn't in the patch, the user edited other fields;
 *    no schedule change → no auto-open.
 *  - If the patch clears the rule (set to undefined), there's nothing to
 *    generate → no auto-open.
 *  - If the patch sets a rule identical to the existing one, schedule
 *    unchanged → no auto-open (rare but possible — explicit no-op save). */
export function shouldAutoOpenOnUpdate(
  previousRule: RecurrenceRule | undefined,
  input: { recurrenceRule?: RecurrenceRule },
): boolean {
  if (!("recurrenceRule" in input)) return false;
  const next = serialiseRule(input.recurrenceRule);
  if (!next) return false;
  return next !== serialiseRule(previousRule);
}
